// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import {
  ageOnDate,
  DOB_MAX_AGE_YEARS,
  DOB_MIN_AGE_YEARS,
  Step1Schema,
  Step2Schema,
  Step3Schema,
  Step4Schema,
  Step5Schema,
  Step6Schema,
  Step7Schema,
  Step8Schema,
  Step9Schema,
  Step10Schema,
  Step11Schema,
  Step12Schema,
  Step13Schema,
  type OnboardingStepAnswer,
} from "@bitebalance/contracts"

/**
 * Wire step numbers (1-13, api-contracts.md / OnboardingStepAnswerSchema)
 * vs. visual display order. The wire contract is untouched forever; what
 * the user SEES is a deliberate subset.
 *
 * ADR-034 (owner decision 2026-08-30): 13 screens scared users away. The
 * shown flow is now the 8 that matter before a first plan — basics/DOB,
 * goal, body+activity, dietary style, allergens, health (safety), time,
 * meal structure. The rest are DEFERRED: submitted automatically at
 * finish with honest, conservative defaults (see DEFERRED_STEPS) and
 * collected later where they have context — Preferences, the Pantry tab,
 * and Amy. The server's all-13 completion gate stays fully satisfied.
 */
export const ALL_WIRE_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13] as const
export type WireStep = (typeof ALL_WIRE_STEPS)[number]

export const VISUAL_STEP_ORDER: readonly WireStep[] = [2, 3, 4, 5, 6, 13, 9, 10]

/**
 * Steps the UI no longer shows, with the defaults submitted at finish.
 * Every default is the honest "nothing declared" value of its schema —
 * never an invented preference: taste lists say "none" (Amy learns them
 * in chat), cooking is the middle of each scale, the weekly spending goal
 * is null (no goal), pantry is "nothing to add now" (the tab is one tap away).
 * Order matters only in that 12 completes onboarding — the flow submits
 * 12 last.
 */
export const DEFERRED_STEPS = [1, 7, 8, 11, 12] as const
export type DeferredStep = (typeof DEFERRED_STEPS)[number]

export function deferredDefaultAnswer(
  step: DeferredStep,
  locale: "de" | "en"
): Record<string, unknown> {
  switch (step) {
    case 1:
      return { language: locale }
    case 7:
      return { neverEat: "none", dislikedCombinations: "none", dislikes: "none", likes: "none" }
    case 8:
      return { cookingEnjoyment: "medium", cookingSkill: "intermediate" }
    case 11:
      // Budget Model v2 (ADR-052): the ONE optional amount is the soft
      // weekly spending goal; null = skipped. No tiers, no maximum here.
      return {
        weeklyGoalEurCents: null,
        preferredRetailers: "none",
        deliveryPreference: "none",
      }
    case 12:
      return { pantryStarter: "nothing_to_add_now", reviewConfirmed: true }
  }
}

export const TOTAL_STEPS = VISUAL_STEP_ORDER.length

export function visualPosition(step: WireStep): number {
  return VISUAL_STEP_ORDER.indexOf(step) + 1
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const STEP_SCHEMAS: Record<WireStep, { safeParse: (v: unknown) => any }> = {
  1: Step1Schema,
  // The DOB stays REQUIRED here (it must agree with the 18+ confirmation;
  // server re-checks with DOB_UNDERAGE). Names became OPTIONAL in the
  // ADR-034 cut — a name is warm but never worth a funnel drop; the
  // profile page's completion banner collects it later.
  2: {
    safeParse: (v: unknown) => {
      const parsed = Step2Schema.safeParse(v)
      if (!parsed.success) return parsed
      const answer = (v as { answer?: { dateOfBirth?: unknown } }).answer
      if (typeof answer?.dateOfBirth !== "string") {
        return { success: false, error: { message: "date of birth requires an explicit answer" } }
      }
      const age = ageOnDate(answer.dateOfBirth)
      if (age < DOB_MIN_AGE_YEARS || age > DOB_MAX_AGE_YEARS) {
        return { success: false, error: { message: "date of birth must imply an age of 18-120" } }
      }
      return parsed
    },
  },
  // Founder device QA P1 item 3 (2026-09-11): `targetAcknowledged` is a
  // plain `z.boolean()` on the wire schema (must accept stored `false`
  // from before this was enforced) but ui-ux-spec.md documents the
  // acknowledgement as required step content — same "wire stays lenient,
  // UI requires an explicit true" pattern as steps 2/4/10 above. Mobile
  // parity: mobile/src/onboarding/steps.ts carries the identical override.
  3: {
    safeParse: (v: unknown) => {
      const parsed = Step3Schema.safeParse(v)
      if (!parsed.success) return parsed
      const answer = (v as { answer?: { targetAcknowledged?: unknown } }).answer
      if (answer?.targetAcknowledged !== true) {
        return {
          success: false,
          error: { message: "target acknowledgement requires an explicit answer" },
        }
      }
      return parsed
    },
  },
  // ADR-027 rule 7: `activity` is OPTIONAL on the wire schema (stored
  // pre-ADR-027 answers have no such key) but the UI requires an explicit
  // base level for anyone passing through the step now — same pattern as
  // step 2's name/DOB and step 10's snackPreference above. Sports/wearable
  // stay genuinely optional (structured but not required).
  4: {
    safeParse: (v: unknown) => {
      const parsed = Step4Schema.safeParse(v)
      if (!parsed.success) return parsed
      const answer = (v as { answer?: { activity?: { level?: unknown } } }).answer
      if (
        answer?.activity === undefined ||
        answer.activity.level === undefined ||
        answer.activity.level === null
      ) {
        return { success: false, error: { message: "activity level requires an explicit answer" } }
      }
      return parsed
    },
  },
  5: Step5Schema,
  6: Step6Schema,
  7: Step7Schema,
  8: Step8Schema,
  9: Step9Schema,
  // ADR-023: snackPreference is OPTIONAL on the wire schema (stored
  // pre-snack answers must keep parsing) but the UI requires an explicit
  // choice — no silent default, consistent with every other step. This
  // client-side gate keeps Continue disabled until one of the four snack
  // options is picked.
  10: {
    safeParse: (v: unknown) => {
      const parsed = Step10Schema.safeParse(v)
      if (!parsed.success) return parsed
      const answer = (v as { answer?: { snackPreference?: unknown } }).answer
      if (answer?.snackPreference === undefined || answer.snackPreference === null) {
        return { success: false, error: { message: "snackPreference requires an explicit answer" } }
      }
      return parsed
    },
  },
  11: Step11Schema,
  12: Step12Schema,
  13: Step13Schema,
}

/** True when `{ step, answer }` validates against that step's contract schema. */
export function isValidStepAnswer(step: WireStep, answer: unknown): answer is OnboardingStepAnswer {
  return STEP_SCHEMAS[step].safeParse({ step, answer }).success
}
